import React, { useState, useEffect, useRef } from "react";
import Modal from "react-modal";
import axios from "axios";
import { ButtonSkeleton } from "./CalendarComponents/SkeletonLoader.jsx";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { DatePicker } from "rsuite";
import moment from "moment";
import crossImage from "../../images/singleinvoice/cross-red.svg";
import infoIcon from "../../images/icons/info-icon.svg";
import { htmlToPlainText } from "./services";
import CalendarIcon from '../ui/CalendarIcon';

const SUPER_ADMIN = 1;
const ADMIN = 2;
const PROJECT_MANAGER = 6;

const ranges = [
    {
        label: "Today",
        value: [new Date(), new Date()],
    },
    {
        label: "Tomorrow",
        value: [moment().add(1, "days").toDate(), moment().add(1, "days").toDate()],
    },
    {
        label: "Next 7 Days",
        value: [new Date(), moment().add(6, "days").toDate()],
    },
];

const MICROSOFT_RECURRENCE_TYPES = {
    daily: 'Daily',
    weekly: 'Weekly',
    absolutemonthly: 'Monthly',
    relativemonthly: 'Monthly',
    absoluteyearly: 'Yearly',
    relativeyearly: 'Yearly',
};

const SHOW_AS_TYPES = [
    { value: 'free', label: 'Free' },
    { value: 'tentative', label: 'Tentative' },
    { value: 'busy', label: 'Busy' },
    { value: 'oof', label: 'Out of Office' },
    { value: 'workingElsewhere', label: 'Working Elsewhere' },
];

const IMPORTANCE_TYPES = [
    { value: 'low', label: 'Low' },
    { value: 'normal', label: 'Normal' },
    { value: 'high', label: 'High' },
];

const SENSITIVITY_TYPES = [
    { value: 'normal', label: 'Normal' },
    { value: 'personal', label: 'Personal' },
    { value: 'private', label: 'Private' },
    { value: 'confidential', label: 'Confidential' },
];

const MicrosoftEditEventModal = ({
    editEventModalOpen,
    setEditEventModalOpen,
    isSubmitLoading,
    setIsSubmitLoading,
    selectedMicrosoftEventForEdit,
    setSelectedMicrosoftEventForEdit,
    setSelectedEvents,
    setExpandedEventIndex,
    setModalIsOpen,
    eventName,
    setEventName,
    repeatType,
    setRepeatType,
    REPEAT_TYPES,
    eventDate,
    setEventDate,
    eventEndDate,
    setEventEndDate,
    eventStartTime,
    setEventStartTime,
    eventEndTime,
    setEventEndTime,
    eventDescription,
    setEventDescription,
    eventLocation,
    setEventLocation,
    user,
    eventOrganizer,
    setEventOrganizer,
    userProfiles,
    userProfileLoading,
    eventAttendees,
    setEventAttendees,
    externalAttendees,
    setExternalAttendees,
    isVirtual,
    setIsVirtual,
    eventMeetingLink,
    setEventMeetingLink,
    reminders,
    setReminders,
    refetchEvents,
    refetchSelectedMicrosoftEvents,
    token,
}) => {
    const [dateRangeType, setDateRangeType] = useState("single");
    const [repeatTypeDropdownOpen, setRepeatTypeDropdownOpen] = useState(false);
    const [organizerDropdownOpen, setOrganizerDropdownOpen] = useState(false);
    const [attendeeInput, setAttendeeInput] = useState('');
    const [attendeeSuggestions, setAttendeeSuggestions] = useState([]);
    const [updateScope, setUpdateScope] = useState('this');
    const [isCancelled, setIsCancelled] = useState(false);
    const [isOnlineMeeting, setIsOnlineMeeting] = useState(false);
    const [onlineMeetingProvider, setOnlineMeetingProvider] = useState('unknown');
    const [showAsDropdownOpen, setShowAsDropdownOpen] = useState(false);
    const [importanceDropdownOpen, setImportanceDropdownOpen] = useState(false);
    const [sensitivityDropdownOpen, setSensitivityDropdownOpen] = useState(false);
    const [showAs, setShowAs] = useState('busy');
    const [importance, setImportance] = useState('normal');
    const [sensitivity, setSensitivity] = useState('normal');
    // const [allowNewTimeProposals, setAllowNewTimeProposals] = useState(true);
    // const [isDraft, setIsDraft] = useState(false);
    const [hideAttendees, setHideAttendees] = useState(false);
    const [instances, setInstances] = useState([]);
    const [etag, setEtag] = useState(null);

    const [eventNameIsValid, setEventNameIsValid] = useState(false);
    const [repeatTypeIsValid, setRepeatTypeIsValid] = useState(false);
    const [eventDateIsValid, setEventDateIsValid] = useState(false);
    const [eventEndDateIsValid, setEventEndDateIsValid] = useState(false);
    const [eventStartTimeIsValid, setEventStartTimeIsValid] = useState(false);
    const [eventEndTimeIsValid, setEventEndTimeIsValid] = useState(false);
    const [reminderMinutesIsValid, setReminderMinutesIsValid] = useState(false);
    const [showAsIsValid, setShowAsIsValid] = useState(false);
    const [importanceIsValid, setImportanceIsValid] = useState(false);
    const [sensitivityIsValid, setSensitivityIsValid] = useState(false);

    const eventNameRef = useRef(null);
    const repeatTypeRef = useRef(null);
    const eventDateRef = useRef(null);
    const eventEndDateRef = useRef(null);
    const eventStartTimeRef = useRef(null);
    const eventEndTimeRef = useRef(null);
    const organizerRef = useRef(null);
    const eventMeetingLinkRef = useRef(null);
    const showAsRef = useRef(null);
    const importanceRef = useRef(null);
    const sensitivityRef = useRef(null);

    const { appSettings } = useSelector((state) => state.appSettings);

    // Initialize form with selected event data
    useEffect(() => {
        if (selectedMicrosoftEventForEdit) {
            setEventName(selectedMicrosoftEventForEdit.summary || '');
            setEventDate(new Date(selectedMicrosoftEventForEdit.start?.dateTime || selectedMicrosoftEventForEdit.start?.date));
            setEventEndDate(selectedMicrosoftEventForEdit.recurrence?.range?.endDate
                ? new Date(selectedMicrosoftEventForEdit.recurrence.range.endDate)
                : null);
            // setEventEndDate(
            //     selectedMicrosoftEventForEdit.recurrence?.range?.endDate
            //         ? new Date(selectedMicrosoftEventForEdit.recurrence.range.endDate)
            //         : selectedMicrosoftEventForEdit.end?.dateTime ||
            //             selectedMicrosoftEventForEdit.end?.date
            //             ? new Date(
            //                 selectedMicrosoftEventForEdit.end?.dateTime ||
            //                 selectedMicrosoftEventForEdit.end?.date
            //             )
            //             : null
            // );
            setEventStartTime(selectedMicrosoftEventForEdit.isAllDay ? '' : new Date(selectedMicrosoftEventForEdit.start?.dateTime));
            setEventEndTime(selectedMicrosoftEventForEdit.isAllDay ? '' : new Date(selectedMicrosoftEventForEdit.end?.dateTime));
            setEventDescription(htmlToPlainText(selectedMicrosoftEventForEdit.description || ''));
            setEventLocation(selectedMicrosoftEventForEdit.location || '');
            setIsVirtual(
                selectedMicrosoftEventForEdit.isOnlineMeeting ||
                !!selectedMicrosoftEventForEdit.onlineMeeting?.joinUrl
            );
            // setEventMeetingLink(selectedMicrosoftEventForEdit.teamsLink || '');
            setEventMeetingLink(selectedMicrosoftEventForEdit.onlineMeeting?.joinUrl || '');
            setEventOrganizer(selectedMicrosoftEventForEdit.organizer?.emailAddress?.address === user.email ? user._id : selectedMicrosoftEventForEdit.organizer?.emailAddress?.address || null);
            setEventAttendees(selectedMicrosoftEventForEdit.attendees?.filter(a => a.email && userProfiles.some(u => u.email === a.email)).map(a => userProfiles.find(u => u.email === a.email)) || []);
            setExternalAttendees(selectedMicrosoftEventForEdit.attendees?.filter(a => a.email && !userProfiles.some(u => u.email === a.email)).map(a => ({
                email: a.email,
                name: a.displayName || null,
                status: a.responseStatus || 'pending',
                reason: null,
                response_date: a.statusTime || null,
                additional_notes: null,
            })) || []);
            setReminders({
                isReminderOn: selectedMicrosoftEventForEdit.reminders?.isReminderOn ?? true,
                reminderMinutesBeforeStart: selectedMicrosoftEventForEdit.reminders?.reminderMinutesBeforeStart || 15,
            });
            setRepeatType(selectedMicrosoftEventForEdit.isAllDay ? 'all_day' : (selectedMicrosoftEventForEdit.recurrence?.pattern?.type?.toLowerCase() || 'does_not_repeat'));
            setUpdateScope(selectedMicrosoftEventForEdit.recurringEventId ? 'this' : 'this');
            setIsCancelled(selectedMicrosoftEventForEdit.isCancelled || false);
            setIsOnlineMeeting(selectedMicrosoftEventForEdit.isOnlineMeeting || false);
            setOnlineMeetingProvider(
                selectedMicrosoftEventForEdit.onlineMeetingProvider || 'unknown'
            );
            setShowAs(selectedMicrosoftEventForEdit.showAs || 'busy');
            setImportance(selectedMicrosoftEventForEdit.importance || 'normal');
            setSensitivity(selectedMicrosoftEventForEdit.sensitivity || 'normal');
            // setAllowNewTimeProposals(
            //     selectedMicrosoftEventForEdit.allowNewTimeProposals ?? true
            // );
            // setIsDraft(selectedMicrosoftEventForEdit.isDraft || false);
            setHideAttendees(selectedMicrosoftEventForEdit.hideAttendees || false);
            setInstances(selectedMicrosoftEventForEdit.instances || []);
            setEtag(selectedMicrosoftEventForEdit.etag || null);
        }
    }, [selectedMicrosoftEventForEdit, user, userProfiles]);

    // Attendee suggestions
    useEffect(() => {
        if (!attendeeInput) return setAttendeeSuggestions([]);

        const selectedInternalIds = eventAttendees.map(a => a._id);
        const selectedExternalEmails = externalAttendees.map(a => a.email.toLowerCase());

        const filtered = userProfiles.filter((user) =>
            user.email.toLowerCase().includes(attendeeInput.toLowerCase()) &&
            !selectedInternalIds.includes(user._id)
        );

        const exactMatch = userProfiles.find(
            (user) => user.email.toLowerCase() === attendeeInput.toLowerCase()
        );

        if (!exactMatch &&
            attendeeInput.includes('@') &&
            attendeeInput.includes('.') &&
            !selectedExternalEmails.includes(attendeeInput.toLowerCase())) {
            filtered.push({
                _id: null,
                email: attendeeInput,
                name: 'External Email',
                external: true,
            });
        }

        setAttendeeSuggestions(filtered);
    }, [attendeeInput, userProfiles, eventAttendees, externalAttendees]);

    const handleDateSelect = (date) => {
        setEventDate(date);
        setEventDateIsValid(false);
    };

    const handleToggleOrganizerDropdown = () => {
        setOrganizerDropdownOpen(!organizerDropdownOpen);
    };

    const handleSelectAttendee = (attendee) => {
        if (attendee.external) {
            if (!externalAttendees.some((a) => a.email === attendee.email)) {
                setExternalAttendees([...externalAttendees, {
                    email: attendee.email,
                    name: null,
                    status: 'none',
                    reason: null,
                    response_date: null,
                    additional_notes: null,
                }]);
            }
        } else {
            if (!eventAttendees.some((a) => a._id === attendee._id)) {
                setEventAttendees([...eventAttendees, attendee]);
            }
        }
        setAttendeeInput('');
        setAttendeeSuggestions([]);
    };

    const handleRemoveAttendee = (idOrEmail, isExternal) => {
        if (!isExternal && idOrEmail === eventOrganizer?._id) {
            return;
        }
        if (isExternal) {
            setExternalAttendees(externalAttendees.filter((a) => a.email !== idOrEmail));
        } else {
            setEventAttendees(eventAttendees.filter((a) => a._id !== idOrEmail));
        }
    };

    const handleToggleRepeatTypeDropdown = () => {
        setRepeatTypeDropdownOpen(!repeatTypeDropdownOpen);
    };

    const handleEditEventSubmit = async (e) => {
        e.preventDefault();

        setEventNameIsValid(false);
        setEventDateIsValid(false);
        setEventStartTimeIsValid(false);
        setEventEndTimeIsValid(false);
        setRepeatTypeIsValid(false);
        setEventEndDateIsValid(false);
        setReminderMinutesIsValid(false);
        setShowAsIsValid(false);
        setImportanceIsValid(false);
        setSensitivityIsValid(false);

        let isValid = true;

        if (!eventName) {
            setEventNameIsValid(true);
            eventNameRef.current?.focus();
            isValid = false;
        }
        if (!eventDate) {
            setEventDateIsValid(true);
            eventDateRef.current?.scrollIntoView({ behavior: "smooth" });
            isValid = false;
        }
        if (!repeatType) {
            setRepeatTypeIsValid(true);
            repeatTypeRef.current?.focus();
            isValid = false;
        }
        if (["daily", "weekly", "monthly"].includes(repeatType) && !eventEndDate) {
            setEventEndDateIsValid(true);
            eventEndDateRef.current?.scrollIntoView({ behavior: "smooth" });
            isValid = false;
        }
        if (["daily", "weekly", "monthly"].includes(repeatType) && eventDate && eventEndDate && moment(eventEndDate).isBefore(moment(eventDate), "day")) {
            toast.error("End date must be on or after the event date!", {
                position: "bottom-right",
                autoClose: 2000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                progress: undefined,
            });
            setEventEndDateIsValid(true);
            eventEndDateRef.current?.focus();
            isValid = false;
        }
        if (repeatType !== "all_day") {
            if (!eventStartTime) {
                setEventStartTimeIsValid(true);
                eventStartTimeRef.current?.focus();
                isValid = false;
            }
            if (!eventEndTime) {
                setEventEndTimeIsValid(true);
                eventEndTimeRef.current?.focus();
                isValid = false;
            }
            if (eventStartTime && eventEndTime && eventDate && (eventEndDate || eventDate)) {
                const startDateTime = moment(eventDate).set({
                    hour: moment(eventStartTime).hour(),
                    minute: moment(eventStartTime).minute(),
                });
                const endDateTime = moment(eventDate).set({
                    hour: moment(eventEndTime).hour(),
                    minute: moment(eventEndTime).minute(),
                });
                if (endDateTime.isSameOrBefore(startDateTime)) {
                    toast.error("End time must be after start time!", {
                        position: "bottom-right",
                        autoClose: 2000,
                        hideProgressBar: false,
                        closeOnClick: true,
                        pauseOnHover: true,
                        draggable: true,
                        progress: undefined,
                    });
                    setEventStartTimeIsValid(true);
                    setEventEndTimeIsValid(true);
                    eventEndTimeRef.current?.focus();
                    isValid = false;
                }
            }
        }
        if (reminders.reminderMinutesBeforeStart < 0 || reminders.reminderMinutesBeforeStart > 40320) {
            setReminderMinutesIsValid(true);
            toast.error("Reminder minutes must be between 0 and 40320 (4 weeks)!", {
                position: "bottom-right",
                autoClose: 2000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                progress: undefined,
            });
            isValid = false;
        }

        if (!showAs) {
            setShowAsIsValid(true);
            isValid = false;
        }
        if (!importance) {
            setImportanceIsValid(true);
            isValid = false;
        }
        if (!sensitivity) {
            setSensitivityIsValid(true);
            isValid = false;
        }

        if (!isValid) {
            toast.error("Please fill all required fields!", {
                position: "bottom-right",
                autoClose: 2000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                progress: undefined,
            });
            return;
        }

        try {
            setIsSubmitLoading(true);
            const startDateTime = moment(eventDate).set({
                hour: eventStartTime ? moment(eventStartTime).hour() : 0,
                minute: eventStartTime ? moment(eventStartTime).minute() : 0,
            });
            const endDateTime = moment(eventDate).set({
                hour: eventEndTime ? moment(eventEndTime).hour() : 23,
                minute: eventEndTime ? moment(eventEndTime).minute() : 59,
            });

            const requestData = {
                eventId: selectedMicrosoftEventForEdit.id,
                subject: eventName,
                start: repeatType === "all_day" ? {
                    dateTime: moment(eventDate).format('YYYY-MM-DD'),
                    timeZone: global.config.getTimeZone() || 'India Standard Time',
                } : {
                    dateTime: startDateTime.toISOString(),
                    timeZone: global.config.getTimeZone() || 'India Standard Time',
                },
                end: repeatType === "all_day" ? {
                    dateTime: moment(eventDate).format('YYYY-MM-DD'),
                    timeZone: global.config.getTimeZone() || 'India Standard Time',
                } : {
                    dateTime: endDateTime.toISOString(),
                    timeZone: global.config.getTimeZone() || 'India Standard Time',
                },
                body: eventDescription ? { contentType: 'HTML', content: eventDescription } : null,
                location: eventLocation ? { displayName: eventLocation } : null,
                attendees: [
                    ...eventAttendees.map(a => ({
                        emailAddress: {
                            address: a.email,
                            name: a.first_name ? `${a.first_name} ${a.last_name || ''}` : a.company_name || a.email,
                        },
                        type: 'required',
                    })),
                    ...externalAttendees.map(a => ({
                        emailAddress: { address: a.email, name: a.name || a.email },
                        type: 'required',
                    })),
                ].filter(a => a.emailAddress.address),
                ...(isVirtual && {
                    isOnlineMeeting: true,
                    onlineMeetingProvider: user.email.endsWith('@gmail.com') || user.email.endsWith('@outlook.com')
                        ? 'teamsForBusiness'
                        : 'teamsForBusiness',
                }),
                showAs,
                importance,
                sensitivity,
                // allowNewTimeProposals,
                // isDraft,
                hideAttendees,
                isCancelled,
                editScope: updateScope === 'this' ? 'thisEvent' : 'thisAndFollowing',
                // ...(updateScope === 'this_and_following' && {
                //     recurringEventId: selectedMicrosoftEventForEdit.recurringEventId || selectedMicrosoftEventForEdit.id,
                //     instanceDate: moment(eventDate).format('YYYY-MM-DD'),
                // }),
                ...((selectedMicrosoftEventForEdit.recurrence) && {
                    recurringEventId: selectedMicrosoftEventForEdit.recurringEventId || selectedMicrosoftEventForEdit.id,
                    instanceDate: moment(eventDate).format('YYYY-MM-DD'),
                }),
                ...(repeatType !== 'does_not_repeat' && repeatType !== 'all_day' && {
                    recurrence: {
                        pattern: {
                            type: repeatType === 'monthly' ? 'absoluteMonthly' : repeatType,
                            interval: 1,
                            ...(repeatType === 'weekly' && { daysOfWeek: [moment(eventDate).format('dddd').toLowerCase()] }),
                            ...(repeatType === 'monthly' && { dayOfMonth: moment(eventDate).date() }),
                            ...(repeatType === 'yearly' && { dayOfMonth: moment(eventDate).date(), month: moment(eventDate).month() + 1 }),
                        },
                        range: {
                            type: 'endDate',
                            startDate: moment(eventDate).format('YYYY-MM-DD'),
                            endDate: eventEndDate ? moment(eventEndDate).format('YYYY-MM-DD') : moment(eventDate).add(5, 'years').format('YYYY-MM-DD'),
                        },
                    },
                }),
                isReminderOn: reminders.isReminderOn,
                reminderMinutesBeforeStart: reminders.reminderMinutesBeforeStart,
                microsoftUserId: selectedMicrosoftEventForEdit.microsoftUserId,
            };

            const response = await axios.patch(`/api/microsoft-calendar/events/edit/${selectedMicrosoftEventForEdit.id}`, requestData, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            global.config.activityLog(
                window.location.href,
                "Microsoft Event Update",
                `Microsoft Calendar event "${eventName}" updated successfully`
            );
            toast.success(response.data.message || "Microsoft Calendar event updated successfully", {
                position: "bottom-right",
                autoClose: 2000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                progress: undefined,
            });

            refetchSelectedMicrosoftEvents();
            handleCloseEditModal();
            setModalIsOpen(false);
            setExpandedEventIndex(null);
            setSelectedEvents([]);
        } catch (error) {
            console.error("Error updating Microsoft Calendar event:", error);
            global.config.slackMessage(error.toString());

            if (error.response?.data?.status === "error") {
                toast.error(error.response.data.message, {
                    position: "bottom-right",
                    autoClose: 2000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                    progress: undefined,
                });
            } else {
                toast.error("Something went wrong", {
                    position: "bottom-right",
                    autoClose: 2000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                    progress: undefined,
                });
            }
        } finally {
            setIsSubmitLoading(false);
        }
    };

    const handleCloseEditModal = () => {
        if (!isSubmitLoading) {
            setEditEventModalOpen(false);
            setSelectedMicrosoftEventForEdit(null);
            setDateRangeType("single");
            setEventStartTime("");
            setEventEndTime("");
            setEventName("");
            setEventDescription("");
            setEventLocation("");
            setEventOrganizer(user._id);
            setEventAttendees([]);
            setExternalAttendees([]);
            setIsVirtual(false);
            setEventMeetingLink("");
            setRepeatType("does_not_repeat");
            setReminders({ isReminderOn: false, reminderMinutesBeforeStart: 15 });
            setEventNameIsValid(false);
            setEventDateIsValid(false);
            setEventStartTimeIsValid(false);
            setEventEndTimeIsValid(false);
            setRepeatTypeIsValid(false);
            setOrganizerDropdownOpen(false);
            setRepeatTypeDropdownOpen(false);
            setUpdateScope('this');
            setIsCancelled(false);
            setIsOnlineMeeting(false);
            setOnlineMeetingProvider('unknown');
            setShowAs('busy');
            setImportance('normal');
            setSensitivity('normal');
            // setAllowNewTimeProposals(true);
            // setIsDraft(false);
            setHideAttendees(false);
            setInstances([]);
            setEtag(null);
            setShowAsIsValid(false);
            setImportanceIsValid(false);
            setSensitivityIsValid(false);
            document.body.classList.remove("ReactModal__Body--open");
        }
    };

    const handleClickOutsideDropdown = (event) => {
        const refs = {
            repeatType: repeatTypeRef.current,
            organizer: organizerRef.current,
            showAs: showAsRef.current,
            importance: importanceRef.current,
            sensitivity: sensitivityRef.current,
        };

        Object.entries(refs).forEach(([key, ref]) => {
            if (!ref) {
                console.warn(`Ref ${key} is null`);
            }
        });

        try {
            if (refs.repeatType && !refs.repeatType.contains(event.target)) {
                setRepeatTypeDropdownOpen(false);
            }
            if (refs.organizer && !refs.organizer.contains(event.target)) {
                setOrganizerDropdownOpen(false);
            }
            if (refs.showAs && !refs.showAs.contains(event.target)) {
                setShowAsDropdownOpen(false);
            }
            if (refs.importance && !refs.importance.contains(event.target)) {
                setImportanceDropdownOpen(false);
            }
            if (refs.sensitivity && !refs.sensitivity.contains(event.target)) {
                setSensitivityDropdownOpen(false);
            }
        } catch (err) {
            console.error('Error in handleClickOutsideDropdown:', err);
        }
    };

    // const isRecurring = selectedMicrosoftEventForEdit?.recurringEventId;
    const isRecurring = !!selectedMicrosoftEventForEdit?.recurrence;

    useEffect(() => {
        if (typeof window === 'undefined' || !document) {
            return;
        }

        document.addEventListener('click', handleClickOutsideDropdown);

        return () => {
            document.removeEventListener('click', handleClickOutsideDropdown);
        };
    }, []);

    return (
        <Modal
            className="client-modal sprint-modal time-off-close calendar-event-create calendar-event-edit"
            isOpen={editEventModalOpen}
            onRequestClose={!isSubmitLoading ? handleCloseEditModal : undefined}
            contentLabel="Edit Microsoft Calendar Event"
            style={{
                content: {
                    height: "50%",
                    width: "30%",
                    position: "fixed",
                    top: "50%",
                    left: "50%",
                },
            }}
        >
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div className="bg-white rounded-lg p-6 w-full max-w-lg">
                    <div className="modal timeoff-modal">
                        <div>
                            <div className="modal-client-head">
                                <h2>Edit Microsoft Calendar Event</h2>
                                <div className="modal-close-invite outter-modal-main">
                                    <a onClick={handleCloseEditModal} disabled={isSubmitLoading}>
                                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
                                            <path d="M1 13L13 1" stroke="black" />
                                            <path d="M1 0.999999L13 13" stroke="black" />
                                        </svg>
                                    </a>
                                </div>
                            </div>
                            <div className="form-field-client-outer">
                                <form onSubmit={handleEditEventSubmit}>
                                    <div className="form-field-client">
                                        <label htmlFor="eventName" className="form-label">
                                            Event Name *
                                        </label>
                                        <div className="inner-sec-cal-form">
                                            <input
                                                id="eventName"
                                                type="text"
                                                value={eventName}
                                                onChange={(e) => setEventName(e.target.value)}
                                                className={eventNameIsValid ? "input-validation-error form-input" : "form-input"}
                                                ref={eventNameRef}
                                            />
                                            {eventNameIsValid && (
                                                <span className="text-red-500 text-xs italic">
                                                    Event name is required
                                                </span>
                                            )}
                                        </div>
                                    </div>

                                    <div className="form-field-client">
                                        <label htmlFor="repeatType" className="form-label">
                                            Repeat Type *
                                        </label>
                                        <div className="inner-sec-cal-form">
                                            <div ref={repeatTypeRef} className={`cursor ${repeatTypeDropdownOpen ? 'open' : ''}`}>
                                                <span
                                                    id="repeat-type-select"
                                                    onClick={handleToggleRepeatTypeDropdown}
                                                    className={`custom-dropdown-user-new custom-dropdown-select ${repeatTypeIsValid ? 'input-validation-error' : ''}`}
                                                >
                                                    {repeatType ? REPEAT_TYPES.find((rt) => rt.value === repeatType)?.label : 'Select'}
                                                </span>
                                                {repeatTypeDropdownOpen && (
                                                    <div className="custom-dropdown-new">
                                                        <ul className="leave-type-list">
                                                            {REPEAT_TYPES.map((rt) => (
                                                                <li
                                                                    key={rt.value}
                                                                    className={repeatType === rt.value ? 'selected-item' : ''}
                                                                    onClick={() => {
                                                                        setRepeatType(rt.value);
                                                                        setRepeatTypeDropdownOpen(false);
                                                                        if (rt.value === "all_day") {
                                                                            setEventStartTime("");
                                                                            setEventEndTime("");
                                                                        }
                                                                        if (!["daily", "weekly", "monthly"].includes(rt.value)) {
                                                                            setEventEndDate(null);
                                                                        }
                                                                    }}
                                                                >
                                                                    {rt.label}
                                                                </li>
                                                            ))}
                                                        </ul>
                                                    </div>
                                                )}
                                            </div>
                                            {repeatTypeIsValid && (
                                                <span className="text-red-500 text-xs italic">
                                                    Repeat type is required
                                                </span>
                                            )}
                                        </div>
                                    </div>

                                    {isRecurring && (
                                        <div className="form-field-client scope-sec">
                                            <label className="form-label">Update Scope *</label>
                                            <div className="inner-sec-cal-form">
                                                <div className="radio-group">
                                                    <label className="radio-label">
                                                        <input
                                                            type="radio"
                                                            value="this"
                                                            checked={updateScope === "this"}
                                                            onChange={(e) => setUpdateScope(e.target.value)}
                                                            className="form-radio"
                                                        />
                                                        This Event
                                                    </label>
                                                    <label className="radio-label">
                                                        <input
                                                            type="radio"
                                                            value="this_and_following"
                                                            checked={updateScope === "this_and_following"}
                                                            onChange={(e) => setUpdateScope(e.target.value)}
                                                            className="form-radio"
                                                        />
                                                        This and Following Events
                                                    </label>
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    <div className="form-field-client">
                                        <label htmlFor="eventDate" className="form-label">
                                            {["daily", "weekly", "monthly"].includes(repeatType) ? "Start Date *" : "Event Date *"}
                                        </label>
                                        <div className="inner-sec-cal-form">
                                            <div className="date-picker-outer date-picker-outer-all">
                                                <div className="custoom-icon-calender custoom-icon-calender-all">
                                                    <div ref={eventDateRef}>
                                                        <DatePicker
                                                            oneTap
                                                            id="eventDate"
                                                            ref={eventDateRef}
                                                            format="dd/MM/yyyy"
                                                            placeholder="Select Date"
                                                            placement="topEnd"
                                                            ranges={ranges}
                                                            className={`project-calender time-off-cal ${eventDateIsValid ? "input-validation-error" : ""}`}
                                                            value={eventDate}
                                                            onChange={handleDateSelect}
                                                            timeZone={global.config.getTimeZone()}
                                                            isoWeek={appSettings.weekStartDay === 'Monday'}
              caretAs={CalendarIcon}
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                            {eventDateIsValid && (
                                                <span className="text-red-500 text-xs italic">
                                                    Event date is required
                                                </span>
                                            )}
                                        </div>
                                    </div>

                                    {["daily", "weekly", "monthly"].includes(repeatType) && (
                                        <div className="form-field-client">
                                            <label htmlFor="eventEndDate" className="form-label">
                                                End Date *
                                            </label>
                                            <div className="inner-sec-cal-form">
                                                <div className="date-picker-outer date-picker-outer-all" ref={eventEndDateRef}>
                                                    <div className="custoom-icon-calender custoom-icon-calender-all">
                                                        <div ref={eventDateRef}>
                                                            <DatePicker
                                                                oneTap
                                                                id="eventEndDate"
                                                                format="dd/MM/yyyy"
                                                                placeholder="Select End Date"
                                                                placement="topEnd"
                                                                value={eventEndDate}
                                                                onChange={(date) => {
                                                                    setEventEndDate(date);
                                                                    setEventEndDateIsValid(false);
                                                                }}
                                                                ranges={ranges}
                                                                className={`project-calender time-off-cal ${eventEndDateIsValid ? "input-validation-error" : ""}`}
                                                                timeZone={global.config.getTimeZone()}
                                                                isoWeek={appSettings.weekStartDay === 'Monday'}
                                                                shouldDisableDate={(date) => moment(date).isBefore(moment(eventDate), 'day')}
              caretAs={CalendarIcon}
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                                {eventEndDateIsValid && (
                                                    <span className="text-red-500 text-xs italic">
                                                        End date is required for recurring events
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    )}

                                    {repeatType !== "all_day" && (
                                        <>
                                            <div className="form-field-client">
                                                <label htmlFor="eventStartTime" className="form-label">
                                                    Start Time *
                                                </label>
                                                {/* <div className="inner-sec-cal-form">
                                                    <div className="date-picker-outer date-picker-outer-all">
                                                        <DatePicker
                                                            id="eventStartTime"
                                                            format="hh:mm aa"
                                                            value={eventStartTime}
                                                            onChange={(time) => {
                                                                setEventStartTime(time);
                                                                setEventStartTimeIsValid(false);
                                                            }}
                                                            className={eventStartTimeIsValid ? "input-validation-error" : ""}
                                                            ref={eventStartTimeRef}
                                                            style={{ width: "100%" }}
                                                            showMeridian
              caretAs={CalendarIcon}
                                                        />
                                                    </div>
                                                    {eventStartTimeIsValid && (
                                                        <span className="text-red-500 text-xs italic">
                                                            Start time is required
                                                        </span>
                                                    )}
                                                </div> */}
                                                <div className="inner-sec-cal-form">
                                                    <input
                                                        id="startTime"
                                                        type="time"
                                                        value={eventStartTime ? moment(eventStartTime).format("HH:mm") : ""}
                                                        onChange={(e) => {
                                                            const [hours, minutes] = e.target.value.split(":").map(Number);
                                                            const updatedStart = moment(eventDate)
                                                                .set({ hour: hours, minute: minutes })
                                                                .toDate();
                                                            setEventStartTime(updatedStart);
                                                        }}
                                                        className={eventStartTimeIsValid ? "input-validation-error form-input" : "form-input"}
                                                        ref={eventStartTimeRef}
                                                    />
                                                    {eventStartTimeIsValid && (
                                                        <span className="text-red-500 text-xs italic">
                                                            Please select a valid start time
                                                        </span>
                                                    )}
                                                </div>
                                            </div>

                                            <div className="form-field-client">
                                                <label htmlFor="eventEndTime" className="form-label">
                                                    End Time *
                                                </label>
                                                {/* <div className="inner-sec-cal-form">
                                                    <div className="date-picker-outer date-picker-outer-all">
                                                        <DatePicker
                                                            id="eventEndTime"
                                                            format="hh:mm aa"
                                                            value={eventEndTime}
                                                            onChange={(time) => {
                                                                setEventEndTime(time);
                                                                setEventEndTimeIsValid(false);
                                                            }}
                                                            className={eventEndTimeIsValid ? "input-validation-error" : ""}
                                                            ref={eventEndTimeRef}
                                                            style={{ width: "100%" }}
                                                            showMeridian
              caretAs={CalendarIcon}
                                                        />
                                                    </div>
                                                    {eventEndTimeIsValid && (
                                                        <span className="text-red-500 text-xs italic">
                                                            End time is required
                                                        </span>
                                                    )}
                                                </div> */}
                                                <div className="inner-sec-cal-form">
                                                    <input
                                                        id="endTime"
                                                        type="time"
                                                        value={eventEndTime ? moment(eventEndTime).format("HH:mm") : ""}
                                                        onChange={(e) => {
                                                            const [hours, minutes] = e.target.value.split(":").map(Number);
                                                            const updatedEnd = moment(eventDate)
                                                                .set({ hour: hours, minute: minutes })
                                                                .toDate();
                                                            setEventEndTime(updatedEnd);
                                                        }}
                                                        className={eventEndTimeIsValid ? "input-validation-error form-input" : "form-input"}
                                                        ref={eventEndTimeRef}
                                                    />
                                                    {eventEndTimeIsValid && (
                                                        <span className="text-red-500 text-xs italic">
                                                            Please select a valid end time
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                        </>
                                    )}

                                    <div className="form-field-client">
                                        <label htmlFor="eventDescription" className="form-label">
                                            Description
                                        </label>
                                        <div className="inner-sec-cal-form">
                                            <textarea
                                                id="eventDescription"
                                                name="description"
                                                className="form-textarea"
                                                rows="3"
                                                value={eventDescription}
                                                onChange={(e) => setEventDescription(e.target.value)}
                                            />
                                        </div>
                                    </div>

                                    <div className="form-field-client">
                                        <label htmlFor="eventLocation" className="form-label">
                                            Location
                                        </label>
                                        <div className="inner-sec-cal-form">
                                            <input
                                                id="eventLocation"
                                                type="text"
                                                value={eventLocation}
                                                onChange={(e) => setEventLocation(e.target.value)}
                                                className="form-input"
                                            />
                                        </div>
                                    </div>

                                    {/* <div className="form-field-client">
                                        <label htmlFor="eventOrganizer" className="form-label">
                                            Organizer *
                                        </label>
                                        <div className="inner-sec-cal-form">
                                            <div ref={organizerRef} className={`cursor ${organizerDropdownOpen ? 'open' : ''}`}>
                                                <span
                                                    id="organizer-select"
                                                    onClick={handleToggleOrganizerDropdown}
                                                    className="custom-dropdown-user-new custom-dropdown-select"
                                                >
                                                    {eventOrganizer
                                                        ? userProfiles.find((u) => u._id === eventOrganizer)?.email || eventOrganizer
                                                        : 'Select'}
                                                </span>
                                                {organizerDropdownOpen && (
                                                    <div className="custom-dropdown-new">
                                                        <ul className="leave-type-list">
                                                            {userProfiles.map((u) => (
                                                                <li
                                                                    key={u._id}
                                                                    className={eventOrganizer === u._id ? 'selected-item' : ''}
                                                                    onClick={() => {
                                                                        setEventOrganizer(u._id);
                                                                        setOrganizerDropdownOpen(false);
                                                                    }}
                                                                >
                                                                    {u.email}
                                                                </li>
                                                            ))}
                                                        </ul>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div> */}

                                    <div className="form-field-client guest-input-wrapper">
                                        <label className="form-label">Guests</label>
                                        <div className="suggession-user">
                                            <input
                                                id="eventAttendees"
                                                type="text"
                                                value={attendeeInput}
                                                onChange={(e) => setAttendeeInput(e.target.value)}
                                                placeholder="Add guests"
                                                className="form-input"
                                                disabled={userProfileLoading}
                                            />
                                            {attendeeSuggestions.length > 0 && (
                                                <ul className="suggestion-dropdown">
                                                    {attendeeSuggestions.map((user, idx) => (
                                                        <li key={user._id || `external-${idx}`} onClick={() => handleSelectAttendee(user)}>
                                                            <span className="email-icon">
                                                                <img
                                                                    src={`/Uploads/lowProfileImage/${user.profile_image}`}
                                                                    alt="Email"
                                                                    onError={(e) => {
                                                                        e.target.onerror = null;
                                                                        e.target.src = "/Uploads/lowProfileImage/user_default.png";
                                                                    }}
                                                                />
                                                            </span>
                                                            <span>{user.email}</span>
                                                            {user.external && <em> (external)</em>}
                                                            {user._id === eventOrganizer && <em> (Organizer)</em>}
                                                        </li>
                                                    ))}
                                                </ul>
                                            )}
                                        </div>
                                    </div>

                                    {(eventAttendees.length > 0 || externalAttendees.length > 0) && (
                                        <div className="form-field-client guest-input-wrapper hide-it">
                                            <label className="form-label"></label>
                                            <div className="selected-guests">
                                                {eventAttendees.map((attendee) => (
                                                    <div key={attendee._id} className="guest-chip">
                                                        <span className={`email-icon ${attendee._id === eventOrganizer ? 'organizer-icon' : ''}`}>
                                                            <img
                                                                src={`/Uploads/lowProfileImage/${attendee.profile_image}`}
                                                                alt="Email"
                                                                onError={(e) => {
                                                                    e.target.onerror = null;
                                                                    e.target.src = "/Uploads/lowProfileImage/user_default.png";
                                                                }}
                                                            />
                                                        </span>
                                                        <span className="guest-chip-name-sec">
                                                            <span className={`attendee-name ${attendee._id === eventOrganizer ? 'organizer-name' : ''}`}>
                                                                {attendee.first_name ? `${attendee.first_name} ${attendee.last_name || ''}` : attendee.company_name || attendee.email}
                                                                {attendee._id === eventOrganizer && <em> (Organizer)</em>}
                                                            </span>
                                                            {attendee._id !== eventOrganizer && (
                                                                <button
                                                                    type="button"
                                                                    onClick={() => handleRemoveAttendee(attendee._id, false)}
                                                                    className=""
                                                                >
                                                                    ×
                                                                </button>
                                                            )}
                                                        </span>
                                                    </div>
                                                ))}
                                                {externalAttendees.map((attendee, i) => (
                                                    <div key={`external-${i}`} className="guest-chip">
                                                        <span className="email-icon">
                                                            <img
                                                                src={`/Uploads/lowProfileImage/user_default.png`}
                                                                alt="Email"
                                                            />
                                                        </span>
                                                        <span className="guest-chip-name-sec">
                                                            <span>{attendee.email} (external)</span>
                                                            <button
                                                                type="button"
                                                                onClick={() => handleRemoveAttendee(attendee.email, true)}
                                                                className=""
                                                            >
                                                                ×
                                                            </button>
                                                        </span>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {/* <div className="form-field-client is-virtual-vent">
                                        <label htmlFor="isVirtual" className="form-label">
                                            Virtual Meeting
                                        </label>
                                        {/* <div className="inner-sec-cal-form">
                                            <label className="checkbox-label">
                                        <input
                                            id="isVirtual"
                                            type="checkbox"
                                            checked={isVirtual}
                                            onChange={(e) => setIsVirtual(e.target.checked)}
                                            className="form-checkbox"
                                        />
                                        {/* Create Microsoft Teams/Skype Meeting
                                            </label>
                                        </div>
                                    </div> */}

                                    {/* {isVirtual && (
                                        <div className="form-field-client">
                                            <label htmlFor="eventMeetingLink" className="form-label">
                                                Meeting Link
                                            </label>
                                            <div className="inner-sec-cal-form">
                                                <input
                                                    id="eventMeetingLink"
                                                    type="text"
                                                    value={eventMeetingLink}
                                                    readOnly
                                                    className="form-input"
                                                    ref={eventMeetingLinkRef}
                                                />
                                            </div>
                                        </div>
                                    )} */}

                                    <div className="form-field-client">
                                        <label htmlFor="showAs" className="form-label">Show As *</label>
                                        <div className="inner-sec-cal-form">
                                            <div ref={showAsRef} className={`cursor ${showAsDropdownOpen ? 'open' : ''}`}>
                                                <span
                                                    id="showAs-select"
                                                    onClick={() => setShowAsDropdownOpen(!showAsDropdownOpen)}
                                                    className={`custom-dropdown-user-new custom-dropdown-select ${showAsIsValid ? 'input-validation-error' : ''}`}
                                                >
                                                    {showAs ? SHOW_AS_TYPES.find((sa) => sa.value === showAs)?.label : 'Select'}
                                                </span>
                                                {showAsDropdownOpen && (
                                                    <div className="custom-dropdown-new">
                                                        <ul className="leave-type-list">
                                                            {SHOW_AS_TYPES.map((sa) => (
                                                                <li
                                                                    key={sa.value}
                                                                    className={showAs === sa.value ? 'selected-item' : ''}
                                                                    onClick={() => {
                                                                        setShowAs(sa.value);
                                                                        setShowAsDropdownOpen(false);
                                                                        setShowAsIsValid(false);
                                                                    }}
                                                                >
                                                                    {sa.label}
                                                                </li>
                                                            ))}
                                                        </ul>
                                                    </div>
                                                )}
                                            </div>
                                            {showAsIsValid && (
                                                <span className="text-red-500 text-xs italic">Show as is required</span>
                                            )}
                                        </div>
                                    </div>

                                    {/* Importance */}
                                    <div className="form-field-client">
                                        <label htmlFor="importance" className="form-label">Importance *</label>
                                        <div className="inner-sec-cal-form">
                                            <div ref={importanceRef} className={`cursor ${importanceDropdownOpen ? 'open' : ''}`}>
                                                <span
                                                    id="importance-select"
                                                    onClick={() => setImportanceDropdownOpen(!importanceDropdownOpen)}
                                                    className={`custom-dropdown-user-new custom-dropdown-select ${importanceIsValid ? 'input-validation-error' : ''}`}
                                                >
                                                    {importance ? IMPORTANCE_TYPES.find((imp) => imp.value === importance)?.label : 'Select'}
                                                </span>
                                                {importanceDropdownOpen && (
                                                    <div className="custom-dropdown-new">
                                                        <ul className="leave-type-list">
                                                            {IMPORTANCE_TYPES.map((imp) => (
                                                                <li
                                                                    key={imp.value}
                                                                    className={importance === imp.value ? 'selected-item' : ''}
                                                                    onClick={() => {
                                                                        setImportance(imp.value);
                                                                        setImportanceDropdownOpen(false);
                                                                        setImportanceIsValid(false);
                                                                    }}
                                                                >
                                                                    {imp.label}
                                                                </li>
                                                            ))}
                                                        </ul>
                                                    </div>
                                                )}
                                            </div>
                                            {importanceIsValid && (
                                                <span className="text-red-500 text-xs italic">Importance is required</span>
                                            )}
                                        </div>
                                    </div>

                                    {/* Sensitivity */}
                                    <div className="form-field-client">
                                        <label htmlFor="sensitivity" className="form-label">Sensitivity *</label>
                                        <div className="inner-sec-cal-form">
                                            <div ref={sensitivityRef} className={`cursor ${sensitivityDropdownOpen ? 'open' : ''}`}>
                                                <span
                                                    id="sensitivity-select"
                                                    onClick={() => setSensitivityDropdownOpen(!sensitivityDropdownOpen)}
                                                    className={`custom-dropdown-user-new custom-dropdown-select ${sensitivityIsValid ? 'input-validation-error' : ''}`}
                                                >
                                                    {sensitivity ? SENSITIVITY_TYPES.find((sen) => sen.value === sensitivity)?.label : 'Select'}
                                                </span>
                                                {sensitivityDropdownOpen && (
                                                    <div className="custom-dropdown-new">
                                                        <ul className="leave-type-list">
                                                            {SENSITIVITY_TYPES.map((sen) => (
                                                                <li
                                                                    key={sen.value}
                                                                    className={sensitivity === sen.value ? 'selected-item' : ''}
                                                                    onClick={() => {
                                                                        setSensitivity(sen.value);
                                                                        setSensitivityDropdownOpen(false);
                                                                        setSensitivityIsValid(false);
                                                                    }}
                                                                >
                                                                    {sen.label}
                                                                </li>
                                                            ))}
                                                        </ul>
                                                    </div>
                                                )}
                                            </div>
                                            {sensitivityIsValid && (
                                                <span className="text-red-500 text-xs italic">Sensitivity is required</span>
                                            )}
                                        </div>
                                    </div>

                                    {/* Allow New Time Proposals */}
                                    {/* <div className="form-field-client">
                                        <label htmlFor="allowNewTimeProposals" className="form-label">Allow New Time Proposals</label>
                                        <div className="inner-sec-cal-form">
                                            <input
                                                id="allowNewTimeProposals"
                                                type="checkbox"
                                                checked={allowNewTimeProposals}
                                                onChange={(e) => setAllowNewTimeProposals(e.target.checked)}
                                                className="form-checkbox"
                                            />
                                        </div>
                                    </div> */}

                                    {/* Is Draft */}
                                    {/* <div className="form-field-client">
                                        <label htmlFor="isDraft" className="form-label">Draft</label>
                                        <div className="inner-sec-cal-form">
                                            <input
                                                id="isDraft"
                                                type="checkbox"
                                                checked={isDraft}
                                                onChange={(e) => setIsDraft(e.target.checked)}
                                                className="form-checkbox"
                                            />
                                        </div>
                                    </div> */}

                                    {/* Hide Attendees */}
                                    <div className="form-field-client is-virtual-vent">
                                        <label htmlFor="hideAttendees" className="form-label">Hide Attendees</label>
                                        <div className="inner-sec-cal-form">
                                            <input
                                                id="hideAttendees"
                                                type="checkbox"
                                                checked={hideAttendees}
                                                onChange={(e) => setHideAttendees(e.target.checked)}
                                                className="form-checkbox"
                                            />
                                        </div>
                                    </div>

                                    <div className="form-field-client new-line">
                                        <label htmlFor="reminderMinutes" className="form-label">
                                            Reminder (minutes before)
                                        </label>
                                        <div className="inner-sec-cal-form">
                                            <input
                                                id="reminderMinutes"
                                                type="number"
                                                value={reminders.reminderMinutesBeforeStart}
                                                onChange={(e) => {
                                                    setReminders({
                                                        ...reminders,
                                                        reminderMinutesBeforeStart: parseInt(e.target.value) || 0,
                                                    });
                                                    setReminderMinutesIsValid(false);
                                                }}
                                                className={reminderMinutesIsValid ? "input-validation-error form-input" : "form-input"}
                                                min="0"
                                                max="40320"
                                            />
                                            {reminderMinutesIsValid && (
                                                <span className="text-red-500 text-xs italic">
                                                    Reminder minutes must be between 0 and 40320
                                                </span>
                                            )}
                                        </div>
                                    </div>

                                    {/* <div className="form-field-client"> */}
                                    <button
                                        type="submit"
                                        disabled={isSubmitLoading}
                                        className="submit-client submit-client-cal"
                                    >
                                        {isSubmitLoading ? (
                                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                                                <ButtonSkeleton width="100px" height="18px" />
                                            </div>
                                        ) : (
                                            "Update Event"
                                        )}
                                    </button>
                                    {/* </div> */}
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </Modal>
    );
};

export default MicrosoftEditEventModal;